/**
 * FR-DATA-04 — localStorage persistence for user progress.
 *
 * Stores the user's unlocked-level pointer and per-rune accuracy under a
 * single, version-namespaced key. The schema is intentionally tiny so that
 * future versions (v2+) can either migrate or coexist by picking a fresh key.
 *
 * Storage-unavailable behavior: if `localStorage` cannot be read or written
 * (private browsing, disabled, quota errors, exotic embedders) the module
 * degrades to an in-memory session. `saveState` becomes a no-op, `loadState`
 * returns the default state, and a single warning is emitted at import time.
 */

export interface RuneAccuracy {
  correct: number;
  total: number;
}

export interface UserState {
  unlockedLevel: number;
  runeAccuracy: Record<string, RuneAccuracy>;
}

interface StoredEnvelope {
  version: number;
  state: UserState;
}

const STORAGE_KEY = 'runeApp.state.v1';
const SCHEMA_VERSION = 1;

function defaultState(): UserState {
  return {
    unlockedLevel: 1,
    runeAccuracy: {},
  };
}

/**
 * Probe localStorage at module init. We use a throwaway key so we don't
 * clobber real data, and we wrap in try/catch because some environments throw
 * on the very first access (e.g. cookies disabled in Safari).
 */
function probeStorage(): boolean {
  try {
    const probeKey = '__runeApp.probe__';
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

// `let`, not const: a successful probe at boot can still flip to false if a
// later `setItem` throws (quota exceeded, browser-side eviction, embedder
// quirk). After the flip, loadState consults `memoryState` and stays
// consistent with whatever the failing saveState last set.
let storageAvailable = probeStorage();
let midSessionFailureWarned = false;

if (!storageAvailable) {
  console.warn('[runeApp] localStorage is unavailable; progress will not be saved this session.');
}

// In-memory fallback used only when localStorage is unavailable. When
// storage works, every `loadState()` re-reads from disk so external mutations
// (other tabs, devtools, tests) stay visible.
let memoryState: UserState = defaultState();

export function isStorageAvailable(): boolean {
  return storageAvailable;
}

export function loadState(): UserState {
  if (!storageAvailable) {
    return cloneState(memoryState);
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return defaultState();
    const parsed: unknown = JSON.parse(raw);
    if (!isValidEnvelope(parsed) || parsed.version !== SCHEMA_VERSION) {
      // Unknown version or malformed payload — fall back to defaults.
      // We deliberately do NOT delete the key: a future migration may want
      // to read the legacy payload. v1 callers just ignore it.
      return defaultState();
    }
    return normalizeState(parsed.state);
  } catch {
    return defaultState();
  }
}

export function saveState(state: UserState): void {
  const next = cloneState(state);
  if (!storageAvailable) {
    memoryState = next;
    return;
  }
  try {
    const envelope: StoredEnvelope = {
      version: SCHEMA_VERSION,
      state: next,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // Quota exceeded or transient failure — fall back to in-memory so the
    // session continues consistently. Flip the availability flag so
    // subsequent loadState() calls read from memoryState too, otherwise
    // they'd return stale localStorage data.
    memoryState = next;
    storageAvailable = false;
    if (!midSessionFailureWarned) {
      midSessionFailureWarned = true;
      console.warn(
        '[runeApp] localStorage write failed mid-session; progress will not be saved going forward.',
      );
    }
  }
}

export function resetState(): void {
  memoryState = defaultState();
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore: in-memory reset is sufficient for the current session.
  }
}

/** Convenience: record one answer for a rune by Latin transliteration key. */
export function recordAnswer(latin: string, correct: boolean): void {
  const state = loadState();
  const existing = state.runeAccuracy[latin] ?? { correct: 0, total: 0 };
  state.runeAccuracy[latin] = {
    correct: existing.correct + (correct ? 1 : 0),
    total: existing.total + 1,
  };
  saveState(state);
}

/** Convenience: monotonically raise the unlocked-level pointer. */
export function unlockLevel(level: number): void {
  const state = loadState();
  if (level > state.unlockedLevel) {
    state.unlockedLevel = level;
    saveState(state);
  }
}

// --- Internals ---------------------------------------------------------------

function isValidEnvelope(value: unknown): value is StoredEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { version?: unknown; state?: unknown };
  if (typeof v.version !== 'number') return false;
  if (typeof v.state !== 'object' || v.state === null) return false;
  const s = v.state as { unlockedLevel?: unknown; runeAccuracy?: unknown };
  if (typeof s.unlockedLevel !== 'number') return false;
  if (typeof s.runeAccuracy !== 'object' || s.runeAccuracy === null) return false;
  return true;
}

/** Coerce a freshly-parsed UserState into a clean, well-typed shape. */
function normalizeState(raw: UserState): UserState {
  const unlockedLevel =
    Number.isFinite(raw.unlockedLevel) && raw.unlockedLevel >= 1
      ? Math.floor(raw.unlockedLevel)
      : 1;
  const runeAccuracy: Record<string, RuneAccuracy> = {};
  for (const [key, entry] of Object.entries(raw.runeAccuracy ?? {})) {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as RuneAccuracy).correct === 'number' &&
      typeof (entry as RuneAccuracy).total === 'number'
    ) {
      const e = entry as RuneAccuracy;
      runeAccuracy[key] = {
        correct: Math.max(0, Math.floor(e.correct)),
        total: Math.max(0, Math.floor(e.total)),
      };
    }
  }
  return { unlockedLevel, runeAccuracy };
}

function cloneState(state: UserState): UserState {
  // Deep-clone each RuneAccuracy: callers expect a fully independent
  // snapshot. A shallow `{ ...runeAccuracy }` would share inner objects,
  // so a caller mutating `state.runeAccuracy[k].correct` would silently
  // mutate the in-memory mirror without ever calling saveState.
  const runeAccuracy: Record<string, RuneAccuracy> = {};
  for (const [key, value] of Object.entries(state.runeAccuracy)) {
    runeAccuracy[key] = { correct: value.correct, total: value.total };
  }
  return {
    unlockedLevel: state.unlockedLevel,
    runeAccuracy,
  };
}
